import { useCallback, useRef } from 'react';
import type { AnnotationShape } from '../types';
import type { AnnotationAction } from '../annotationReducer';
import { getShapeBounds } from './useShapeResize';

interface RotateState {
  shapeId: string;
  centerScreenX: number;
  centerScreenY: number;
  startAngle: number; // radians
  originalRotation: number; // degrees
}

interface UseShapeRotateOptions {
  shapes: AnnotationShape[];
  dispatch: React.Dispatch<AnnotationAction>;
  containerWidth: number;
  containerHeight: number;
}

export function useShapeRotate({ shapes, dispatch, containerWidth, containerHeight }: UseShapeRotateOptions) {
  const rotateState = useRef<RotateState | null>(null);

  const startRotate = useCallback(
    (shapeId: string, e: React.PointerEvent) => {
      const shape = shapes.find((s) => s.id === shapeId);
      if (!shape) return;

      const bounds = getShapeBounds(shape);
      const centerNormX = bounds.x + bounds.width / 2;
      const centerNormY = bounds.y + bounds.height / 2;

      // Convert normalized center to screen coordinates
      const svg = (e.target as SVGElement).closest('svg');
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const screenCenterX = rect.left + centerNormX * rect.width;
      const screenCenterY = rect.top + centerNormY * rect.height;

      const startAngle = Math.atan2(
        e.clientY - screenCenterY,
        e.clientX - screenCenterX,
      );

      rotateState.current = {
        shapeId,
        centerScreenX: screenCenterX,
        centerScreenY: screenCenterY,
        startAngle,
        originalRotation: shape.rotation ?? 0,
      };
    },
    [shapes],
  );

  const onRotateMove = useCallback(
    (clientX: number, clientY: number, shiftKey: boolean) => {
      const state = rotateState.current;
      if (!state) return;

      const currentAngle = Math.atan2(
        clientY - state.centerScreenY,
        clientX - state.centerScreenX,
      );

      const deltaAngle = (currentAngle - state.startAngle) * (180 / Math.PI);
      let newRotation = state.originalRotation + deltaAngle;

      // Shift: snap to 15-degree increments
      if (shiftKey) {
        newRotation = Math.round(newRotation / 15) * 15;
      }

      // Normalize to 0-360
      newRotation = ((newRotation % 360) + 360) % 360;

      dispatch({ type: 'ROTATE_SHAPE', id: state.shapeId, rotation: newRotation });
    },
    [dispatch],
  );

  const endRotate = useCallback(() => {
    rotateState.current = null;
  }, []);

  const isRotating = useCallback(() => rotateState.current !== null, []);

  return { startRotate, onRotateMove, endRotate, isRotating };
}
